import {createFlightTap} from './flight-input.mjs';
/** Desktop pointer-lock plus touch/drag fallback. No browser globals captured at import. */
export function installWorldControls({
  canvas,
  doc = globalThis.document,
  win = globalThis.window,
  // Retained for callers that still supply it. Nonmodal panels do not own game input.
  isPanelOpen: _isPanelOpen = () => false,
  onLook = () => {},
  onInteract = () => {},
  onShortcut = () => {},
  onLockChange = () => {},
  onNotice = () => {},
  onZoom = () => {},
  onClear = () => {},
  flightAllowed = () => false,
  isFlying = () => false,
  now = () => globalThis.performance.now(),
}) {
  const keys = new Set(), off = [], flightTap=createFlightTap({now});
  const movementKeys = new Set(['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright', ' ', 'shift']);
  const uiTags = new Set(['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'SUMMARY']);
  const uiRoles = new Set(['button', 'link', 'textbox', 'searchbox', 'combobox', 'listbox', 'option', 'checkbox', 'radio', 'switch', 'slider', 'spinbutton', 'menuitem', 'menuitemcheckbox', 'menuitemradio', 'tab', 'treeitem']);
  const touchPointers = new Map();
  let pinchDistance = 0;
  let dragging = false, dragPointerId = null, lastX = 0, lastY = 0;
  let disposed = false, pending = null, captureWanted = false, requestVersion = 0;
  let focused = doc.hasFocus?.() ?? true;

  const listen = (target, event, fn, options) => {
    target.addEventListener(event, fn, options);
    off.push(() => target.removeEventListener(event, fn, options));
  };
  const locked = () => doc.pointerLockElement === canvas;
  const coarse = () => win.matchMedia?.('(pointer: coarse)').matches === true;
  const available = () => !disposed && focused && !doc.hidden;
  const interactive = (node) => {
    // Inspect ancestors too: an icon/span in a button and a shadow-DOM editor are UI.
    for (let current = node; current && current !== canvas; current = current.parentElement || current.getRootNode?.()?.host) {
      if (uiTags.has(current.tagName) || current.isContentEditable || current.tabIndex >= 0) return true;
      if (uiRoles.has(current.getAttribute?.('role')?.toLowerCase())) return true;
      if (['A', 'AREA'].includes(current.tagName) && current.hasAttribute?.('href')) return true;
      if (['AUDIO', 'VIDEO'].includes(current.tagName) && current.hasAttribute?.('controls')) return true;
    }
    return false;
  };
  const eventInUI = (event) => interactive(event.target) || event.composedPath?.().some(interactive);
  const gameFocused = () => available() && !interactive(doc.activeElement) && (locked() || doc.activeElement === canvas);
  const stopDragging = () => {
    const pointerId = dragPointerId;
    dragging = false;
    dragPointerId = null;
    if (pointerId !== null) {
      try { canvas.releasePointerCapture?.(pointerId); } catch { /* Capture may already have ended. */ }
    }
  };
  const stopTouches = () => {
    const ids = [...touchPointers.keys()]; touchPointers.clear(); pinchDistance = 0;
    for (const id of ids) { try { canvas.releasePointerCapture?.(id); } catch {} }
  };
  const clear = () => {
    keys.clear();
    flightTap.clear();
    stopTouches();
    stopDragging();
    onClear();
  };
  const releasePointer = () => {
    // requestPointerLock is asynchronous. An old request must not steal focus back from UI.
    captureWanted = false;
    requestVersion++;
    clear();
    if (locked()) doc.exitPointerLock?.();
  };
  const requestCapture = async () => {
    if (!available()) return;
    canvas.focus?.({ preventScroll: true });
    if (pending !== null || locked() || coarse()) return;
    if (typeof canvas.requestPointerLock !== 'function') {
      onNotice('Mouse capture is unavailable here. Drag on the world to look.');
      return;
    }
    const version = ++requestVersion;
    pending = version;
    captureWanted = true;
    try {
      await canvas.requestPointerLock();
      if ((!captureWanted || !available() || interactive(doc.activeElement)) && locked()) doc.exitPointerLock?.();
    } catch {
      if (!disposed && version === requestVersion && captureWanted) {
        captureWanted = false;
        onNotice('Mouse capture was not available. Click the world to retry, or drag to look.');
      }
    } finally {
      if (pending === version) pending = null;
    }
  };

  listen(canvas, 'click', () => { requestCapture(); });
  listen(canvas, 'pointerdown', (event) => {
    if (event.pointerType === 'touch' && touchPointers.size >= 2) { event.preventDefault(); return; }
    if (event.pointerType === 'touch' && available() && !locked() && touchPointers.size < 2) {
      canvas.focus?.({ preventScroll: true });
      touchPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      canvas.setPointerCapture?.(event.pointerId);
      if (touchPointers.size === 2) {
        event.preventDefault();
        dragging = false; dragPointerId = null;
        const [a, b] = [...touchPointers.values()]; pinchDistance = Math.hypot(a.x - b.x, a.y - b.y);
        return;
      }
    }
    if (!available() || locked() || dragging || ![0, 2].includes(event.button)) return;
    canvas.focus?.({ preventScroll: true });
    dragging = true;
    dragPointerId = event.pointerId ?? null;
    lastX = event.clientX;
    lastY = event.clientY;
    canvas.setPointerCapture?.(event.pointerId);
  });
  listen(canvas, 'pointermove', (event) => {
    if (touchPointers.has(event.pointerId)) {
      touchPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (touchPointers.size === 2) {
        if (!gameFocused()) return;
        event.preventDefault();
        const [a, b] = [...touchPointers.values()], distance = Math.hypot(a.x - b.x, a.y - b.y);
        if (pinchDistance > 0) onZoom((pinchDistance - distance) * 6);
        pinchDistance = distance;
        return;
      }
    }
    if (!dragging || !gameFocused() || locked() || (event.pointerId !== undefined && event.pointerId !== dragPointerId)) return;
    const dx = event.clientX - lastX, dy = event.clientY - lastY;
    lastX = event.clientX;
    lastY = event.clientY;
    onLook(dx, dy, 'drag');
  });
  listen(canvas, 'pointerup', (event) => {
    if (touchPointers.has(event.pointerId)) { const pinching = touchPointers.size === 2; stopTouches(); if (pinching) { stopDragging(); return; } }
    if (event.pointerId === undefined || event.pointerId === dragPointerId) stopDragging();
  });
  listen(canvas, 'pointercancel', clear);
  listen(canvas, 'lostpointercapture', (event) => {
    if (touchPointers.has(event.pointerId)) { stopTouches(); stopDragging(); }
    if (event.pointerId === dragPointerId) stopDragging();
  });
  listen(canvas, 'blur', releasePointer);
  listen(doc, 'mousemove', (event) => {
    if (locked() && gameFocused()) onLook(event.movementX || 0, event.movementY || 0, 'locked');
  });
  listen(doc, 'pointerlockchange', () => {
    clear();
    if (locked() && (!captureWanted || !available() || interactive(doc.activeElement))) {
      doc.exitPointerLock?.();
      return;
    }
    if (!locked()) captureWanted = false;
    onLockChange(locked());
  });
  listen(doc, 'pointerlockerror', () => {
    const wasWanted = captureWanted;
    captureWanted = false;
    pending = null;
    clear();
    onLockChange(false);
    if (wasWanted) onNotice('Mouse capture is unavailable. You can still drag to look.');
  });
  listen(doc, 'focusin', (event) => {
    if (eventInUI(event)) releasePointer();
  });
  listen(canvas, 'contextmenu', (event) => event.preventDefault());
  listen(canvas, 'wheel', (event) => {
    if (!available()) return;
    onZoom(event.deltaY);
    event.preventDefault();
  }, { passive: false });
  listen(win, 'keydown', (event) => {
    if (!available() || event.isComposing || event.keyCode === 229) return;
    const key = event.key?.toLowerCase();
    if (key === 'escape') {
      releasePointer();
      if (!event.repeat) {
        const active = doc.activeElement;
        const fromInteractive = !!(interactive(active) || eventInUI(event));
        if (interactive(active)) active.blur?.();
        event.preventDefault();
        onShortcut('escape', { fromInteractive });
      }
      return;
    }
    if (eventInUI(event) || !gameFocused()) return;
    if (key === 'tab') {
      releasePointer();
      return; // Let the browser continue normal keyboard focus navigation.
    }
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (key === 'enter') {
      if (!event.repeat) {
        event.preventDefault();
        releasePointer();
        onShortcut('enter');
      }
      return;
    }
    if (movementKeys.has(key) || (key === 'c' && isFlying())) {
      // A held key can keep repeating after Escape, a panel opening or focus loss.
      // Only a fresh press may restart movement after clear(), even without lock.
      if (event.repeat && !keys.has(key)) return;
      if (key === ' ' || key.startsWith('arrow')) event.preventDefault();
      if(key===' '&&!event.repeat&&!event.ctrlKey&&!keys.has(key)&&flightAllowed())flightTap.press();
      keys.add(key);
    }
    if (!event.repeat) {
      if (key === 'e') onInteract();
      else if (['v', 'c', 'r', 'q', 'x'].includes(key) && !(key === 'c' && isFlying())) onShortcut(key);
    }
  });
  listen(win, 'keyup', (event) => keys.delete(event.key?.toLowerCase()));
  listen(win, 'blur', () => { focused = false; releasePointer(); });
  listen(win, 'focus', () => { focused = true; });
  for (const type of ['resize', 'orientationchange', 'pagehide']) listen(win, type, releasePointer);
  listen(doc, 'visibilitychange', () => { if (doc.hidden) releasePointer(); });

  return {
    keys, clear, releasePointer, requestCapture, isLocked: locked,
    consumeFlightToggle:()=>flightTap.consume(),
    setKey(key, down) {
      key = key.toLowerCase();
      if (!down) { keys.delete(key); return; }
      if (!available()) return;
      // Touch game buttons are an explicit return to the world, including from an editor.
      canvas.focus?.({ preventScroll: true });
      if (gameFocused()) keys.add(key);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      releasePointer();
      off.forEach((fn) => fn());
    },
  };
}
